import { useState } from "react";
import { Scissors, Check, Crown, Loader2, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { startCheckout } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const TIERS = [
  { key: null, yearKey: null, name: "Free", price: 0, yearPrice: 0, tag: "Start here",
    features: ["2 shows / month", "Watermarked clips", "720p exports", "Style DNA (1 upload)"] },
  { key: "creator_monthly", yearKey: "creator_yearly", name: "Creator", price: 29, yearPrice: 290, tag: "For working comics",
    features: ["Unlimited shows", "No watermark", "1080p exports", "Full Style DNA + Keep/Kill/Love", "Expand & Moment Finder", "Top Performers dashboard"] },
  { key: "pro_monthly", yearKey: "pro_yearly", name: "Pro", price: 79, yearPrice: 790, tag: "Most popular", best: true,
    features: ["Everything in Creator", "Priority processing", "Bulk & per-guest reel exports", "Reference Clip Mode", "Early access to new tools"] },
  { key: "agency", yearKey: "agency", name: "Agency", price: null, yearPrice: null, tag: "Teams & white-label",
    features: ["Everything in Pro", "Team seats", "White-label share pages", "Shared roster & workspace", "Dedicated support"] },
];

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(null);
  const [yearly, setYearly] = useState(false);

  const choose = async (key) => {
    if (!key) { navigate("/"); return; }
    if (key === "agency") { window.location.href = "mailto:hello@clipminer.app?subject=Agency%20plan"; return; }
    if (!user) { localStorage.setItem("intended_plan", key); const r = window.location.origin + "/"; window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(r)}`; return; }
    setLoading(key);
    try {
      const { checkout_url } = await startCheckout(window.location.origin, key);
      window.location.href = checkout_url;
    } catch { toast.error("Could not start checkout"); setLoading(null); }
  };

  return (
    <div className="min-h-screen bg-background text-white" data-testid="pricing-page">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <a href="/" className="flex items-center gap-3">
            <div className="grid h-9 w-9 place-items-center bg-accent"><Scissors className="h-4 w-4 text-black" /></div>
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-accent">Clip Miner</span>
          </a>
          <button onClick={() => navigate("/")} className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-muted-foreground hover:text-white">
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </button>
        </div>
      </header>

      <section className="mx-auto max-w-6xl px-6 py-14">
        <h1 className="font-display text-4xl tracking-tight sm:text-5xl">Simple plans that <span className="text-accent">grow with you</span></h1>
        <p className="mt-4 max-w-xl font-body text-base text-muted-foreground">Start free. Upgrade when your clips start pulling a crowd. Cancel anytime.</p>

        <div className="mt-8 flex items-center gap-3" data-testid="billing-toggle">
          <span className={`font-mono text-[11px] uppercase tracking-widest ${!yearly ? "text-white" : "text-muted-foreground"}`}>Monthly</span>
          <button data-testid="billing-toggle-btn" onClick={() => setYearly((y) => !y)}
                  className={`relative h-6 w-11 rounded-full transition-colors ${yearly ? "bg-accent" : "bg-secondary"}`}>
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-black transition-transform ${yearly ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
          <span className={`font-mono text-[11px] uppercase tracking-widest ${yearly ? "text-white" : "text-muted-foreground"}`}>
            Yearly <span className="text-accent">· 2 months free</span>
          </span>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
          {TIERS.map((t) => {
            const activeKey = yearly ? t.yearKey : t.key;
            const price = yearly ? t.yearPrice : t.price;
            return (
            <div key={t.name} data-testid={`tier-${t.name.toLowerCase()}`}
                 className={`flex flex-col border p-6 ${t.best ? "border-accent bg-accent/[0.05]" : "border-border bg-card"}`}>
              {t.best && <span className="mb-3 w-fit bg-accent px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest text-black">{t.tag}</span>}
              {!t.best && <span className="mb-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{t.tag}</span>}
              <h2 className="font-display text-2xl tracking-wide">{t.name}</h2>
              <p className="mt-2 font-display text-3xl">
                {price === null ? "Let's talk" : price === 0 ? "$0" : <>${price}<span className="font-body text-sm text-muted-foreground">/{yearly ? "yr" : "mo"}</span></>}
              </p>
              <ul className="mt-5 flex-1 space-y-2.5">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 font-body text-sm text-white/85">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" /> {f}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`tier-cta-${t.name.toLowerCase()}`}
                onClick={() => choose(activeKey)}
                disabled={loading === activeKey}
                className={`mt-6 flex items-center justify-center gap-2 py-3 font-body text-sm font-semibold transition-transform hover:-translate-y-0.5 disabled:opacity-60 ${
                  t.best ? "bg-accent text-black" : t.key ? "border border-accent text-accent" : "border border-border text-white"}`}
              >
                {loading === activeKey ? <Loader2 className="h-4 w-4 animate-spin" /> : t.key && t.key !== "agency" ? <Crown className="h-4 w-4" /> : null}
                {!t.key ? "Get started free" : t.key === "agency" ? "Contact us" : `Choose ${t.name}`}
              </button>
            </div>
            );
          })}
        </div>
        <p className="mt-6 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Secure checkout via Stripe · tax handled automatically · cancel anytime</p>
      </section>
    </div>
  );
}
